/**
 * WGN Smart Tray Configurator — geometry-driven engine
 * Isolation: every instance is scoped to [data-wgn-smart-tray] root + sectionId.
 * Geometry source of truth: merchant-pasted Export Configurator Layout JSON.
 * Texture model: one universal master texture per product, clipped per slot polygon.
 */
(function (global) {
  'use strict';

  var NS = 'WgnSmartTray';
  var MIN_SLOTS = 1;
  var MAX_SLOTS = 24;
  // Float noise only — meaningful OOB still rejects
  var COORD_TOLERANCE = 0.75;
  var activeEngines = [];

  function svgNs() { return 'http://www.w3.org/2000/svg'; }

  function polygonArea(points) {
    var a = 0;
    for (var i = 0, j = points.length - 1; i < points.length; j = i++) {
      a += points[j][0] * points[i][1] - points[i][0] * points[j][1];
    }
    return Math.abs(a / 2);
  }

  function segmentsIntersect(a, b, c, d) {
    function cross(p, q, r) {
      return (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
    }
    function onSeg(p, q, r) {
      return Math.min(p[0], r[0]) - 1e-9 <= q[0] && q[0] <= Math.max(p[0], r[0]) + 1e-9 &&
        Math.min(p[1], r[1]) - 1e-9 <= q[1] && q[1] <= Math.max(p[1], r[1]) + 1e-9;
    }
    var d1 = cross(a, b, c), d2 = cross(a, b, d), d3 = cross(c, d, a), d4 = cross(c, d, b);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
    if (Math.abs(d1) < 1e-9 && onSeg(a, c, b)) return true;
    if (Math.abs(d2) < 1e-9 && onSeg(a, d, b)) return true;
    if (Math.abs(d3) < 1e-9 && onSeg(c, a, d)) return true;
    if (Math.abs(d4) < 1e-9 && onSeg(c, b, d)) return true;
    return false;
  }

  function isSelfIntersecting(points) {
    var n = points.length;
    if (n < 4) return false;
    for (var i = 0; i < n; i++) {
      var a1 = points[i], a2 = points[(i + 1) % n];
      for (var j = i + 1; j < n; j++) {
        if (Math.abs(i - j) <= 1 || (i === 0 && j === n - 1) || (j === 0 && i === n - 1)) continue;
        if ((i + 1) % n === j || (j + 1) % n === i) continue;
        var b1 = points[j], b2 = points[(j + 1) % n];
        if (segmentsIntersect(a1, a2, b1, b2)) return true;
      }
    }
    return false;
  }

  function pointInPoly(pt, poly) {
    var inside = false;
    for (var i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      var xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
      var intersect = ((yi > pt[1]) !== (yj > pt[1])) &&
        (pt[0] < (xj - xi) * (pt[1] - yi) / ((yj - yi) || 1e-12) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // Proper crossing only — shared compartment edges / touching vertices are allowed
  function segmentsProperIntersect(a, b, c, d) {
    function cross(p, q, r) {
      return (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
    }
    var d1 = cross(a, b, c), d2 = cross(a, b, d), d3 = cross(c, d, a), d4 = cross(c, d, b);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
  }

  function edgesProperlyCross(a, b) {
    var i, j;
    for (i = 0; i < a.length; i++) {
      var a1 = a[i], a2 = a[(i + 1) % a.length];
      for (j = 0; j < b.length; j++) {
        var b1 = b[j], b2 = b[(j + 1) % b.length];
        if (segmentsProperIntersect(a1, a2, b1, b2)) return true;
      }
    }
    return false;
  }

  function sampleStrictInterior(poly) {
    var cx = 0, cy = 0, i;
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (i = 0; i < poly.length; i++) {
      cx += poly[i][0];
      cy += poly[i][1];
      minX = Math.min(minX, poly[i][0]);
      minY = Math.min(minY, poly[i][1]);
      maxX = Math.max(maxX, poly[i][0]);
      maxY = Math.max(maxY, poly[i][1]);
    }
    cx /= poly.length;
    cy /= poly.length;
    var samples = [[cx, cy]];
    // Pull edge midpoints toward centroid so shared boundaries are not sampled
    for (i = 0; i < poly.length; i++) {
      var nx = (poly[i][0] + poly[(i + 1) % poly.length][0]) / 2;
      var ny = (poly[i][1] + poly[(i + 1) % poly.length][1]) / 2;
      samples.push([nx * 0.25 + cx * 0.75, ny * 0.25 + cy * 0.75]);
    }
    // Dense bbox grid of candidate interior points (catches partial overlaps
    // where centroids do not land inside each other)
    var gx, gy;
    for (gy = 0; gy <= 6; gy++) {
      for (gx = 0; gx <= 6; gx++) {
        var px = minX + (maxX - minX) * (gx / 6);
        var py = minY + (maxY - minY) * (gy / 6);
        // nudge toward centroid to avoid boundary false positives
        px = px * 0.85 + cx * 0.15;
        py = py * 0.85 + cy * 0.15;
        if (pointInPoly([px, py], poly)) samples.push([px, py]);
      }
    }
    return samples;
  }

  function polysOverlapMeaningfully(a, b) {
    // Interior sample test — rejects true area overlap.
    // Shared / near-shared traced edges are allowed (floating-point "crossings"
    // along dividers are treated as warnings by the caller, not hard errors).
    var samples = sampleStrictInterior(a);
    var i;
    for (i = 0; i < samples.length; i++) {
      if (pointInPoly(samples[i], b)) return true;
    }
    samples = sampleStrictInterior(b);
    for (i = 0; i < samples.length; i++) {
      if (pointInPoly(samples[i], a)) return true;
    }
    return false;
  }

  /**
   * parseAndValidateTrayLayout — canonical layout parser
   * Accepts Export Configurator Layout JSON string or object.
   */
  function parseAndValidateTrayLayout(input) {
    var errors = [];
    var warnings = [];
    var raw = input;

    if (raw == null || (typeof raw === 'string' && !String(raw).trim())) {
      return { ok: false, errors: ['Layout JSON is empty.'], layout: null };
    }

    var data;
    try {
      data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      return { ok: false, errors: ['Invalid JSON syntax: ' + (e.message || e)], layout: null };
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return { ok: false, errors: ['Layout must be a JSON object.'], layout: null };
    }

    var version = data.version != null ? Number(data.version) : 1;
    if (!Number.isFinite(version) || version < 1 || version > 1) {
      errors.push('Unsupported layout version (expected 1).');
    }

    var vb = data.viewBox || {};
    var vw = Number(vb.width != null ? vb.width : vb.w);
    var vh = Number(vb.height != null ? vb.height : vb.h);
    if (!Number.isFinite(vw) || vw <= 0 || !Number.isFinite(vh) || vh <= 0) {
      errors.push('viewBox.width and viewBox.height must be positive finite numbers.');
    }

    if (!Array.isArray(data.slots)) {
      errors.push('slots must be an array.');
      return { ok: false, errors: errors, layout: null };
    }

    if (data.slots.length < MIN_SLOTS || data.slots.length > MAX_SLOTS) {
      errors.push('Slot count must be between ' + MIN_SLOTS + ' and ' + MAX_SLOTS + ' (got ' + data.slots.length + ').');
    }

    var seen = Object.create(null);
    var slots = [];

    data.slots.forEach(function (slot, idx) {
      if (!slot || typeof slot !== 'object') {
        errors.push('Slot at index ' + idx + ' is invalid.');
        return;
      }
      var num = Number(slot.number);
      if (!Number.isInteger(num) || num < 1) {
        errors.push('Slot at index ' + idx + ' needs a unique positive integer number.');
        return;
      }
      if (seen[num]) {
        errors.push('Duplicate slot number: ' + num);
        return;
      }
      seen[num] = true;

      if (!Array.isArray(slot.points) || slot.points.length < 3) {
        errors.push('Slot ' + num + ' needs at least 3 points.');
        return;
      }

      var pts = [];
      var uniq = Object.create(null);
      for (var p = 0; p < slot.points.length; p++) {
        var pt = slot.points[p];
        if (!Array.isArray(pt) || pt.length < 2) {
          errors.push('Slot ' + num + ' point ' + p + ' must be [x,y].');
          return;
        }
        var x = Number(pt[0]), y = Number(pt[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          errors.push('Slot ' + num + ' has non-finite coordinates.');
          return;
        }
        if (Number.isFinite(vw) && Number.isFinite(vh)) {
          if (x < -COORD_TOLERANCE || y < -COORD_TOLERANCE ||
              x > vw + COORD_TOLERANCE || y > vh + COORD_TOLERANCE) {
            errors.push('Slot ' + num + ' has out-of-bounds coordinates (viewBox ' + vw + '×' + vh + ').');
            return;
          }
        }
        pts.push([x, y]);
        uniq[x + ',' + y] = true;
      }
      if (Object.keys(uniq).length < 3) {
        errors.push('Slot ' + num + ' needs at least 3 unique vertices.');
        return;
      }

      var area = polygonArea(pts);
      if (area < 1) {
        errors.push('Slot ' + num + ' has degenerate (near-zero) area.');
        return;
      }
      if (isSelfIntersecting(pts)) {
        errors.push('Slot ' + num + ' is self-intersecting.');
        return;
      }

      slots.push({
        number: num,
        name: slot.name || ('Slot ' + num),
        points: pts,
        area: area
      });
    });

    slots.sort(function (a, b) { return a.number - b.number; });

    // Require sequential 1..N with no gaps (merchant physical compartments)
    if (slots.length && errors.length === 0) {
      for (var s = 0; s < slots.length; s++) {
        if (slots[s].number !== s + 1) {
          errors.push('Slot numbers must be sequential starting at 1 with no gaps (expected ' + (s + 1) + ', found ' + slots[s].number + ').');
          break;
        }
      }
    }

    for (var i = 0; i < slots.length; i++) {
      for (var j = i + 1; j < slots.length; j++) {
        if (polysOverlapMeaningfully(slots[i].points, slots[j].points)) {
          errors.push('Slots ' + slots[i].number + ' and ' + slots[j].number + ' overlap (invalid physical compartments).');
        } else if (edgesProperlyCross(slots[i].points, slots[j].points)) {
          warnings.push('Slots ' + slots[i].number + ' and ' + slots[j].number + ' have near-crossing edges (check tracing if dividers look wrong).');
        }
      }
    }

    if (errors.length) {
      return { ok: false, errors: errors, warnings: warnings, layout: null };
    }

    return {
      ok: true,
      errors: [],
      warnings: warnings,
      layout: {
        version: 1,
        viewBox: { width: vw, height: vh },
        slots: slots
      }
    };
  }

  function pointsAttr(points) {
    return points.map(function (p) { return p[0] + ',' + p[1]; }).join(' ');
  }

  function centroid(points) {
    var x = 0, y = 0;
    for (var i = 0; i < points.length; i++) { x += points[i][0]; y += points[i][1]; }
    return { x: x / points.length, y: y / points.length };
  }

  function createEl(tag, attrs) {
    var el = document.createElementNS(svgNs(), tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] != null) el.setAttribute(k, attrs[k]);
      });
    }
    return el;
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      if (!src) return reject(new Error('Missing image URL'));
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        var finish = function () {
          if (!img.naturalWidth || !img.naturalHeight) {
            reject(new Error('Image decoded with zero dimensions: ' + src));
            return;
          }
          resolve(img);
        };
        if (typeof img.decode === 'function') {
          img.decode().then(finish).catch(function () {
            // decode can fail for some SVG/data URLs; fall back to onload dimensions
            finish();
          });
        } else {
          finish();
        }
      };
      img.onerror = function () { reject(new Error('Failed to load: ' + src)); };
      img.src = src;
    });
  }

  function moneyFormat(cents, currency) {
    if (!Number.isFinite(cents) || cents <= 0) return '';
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency || 'USD' });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function WgnSmartTrayEngine(root, boot) {
    this.root = root;
    this.sectionId = boot.sectionId;
    this.currency = boot.currency || 'USD';
    this.cartAddUrl = boot.cartAddUrl || '/cart/add.js';
    this.cartUrl = boot.cartUrl || '/cart';
    this.imgbbKey = boot.imgbbKey || '';
    this.soldOutBehavior = boot.soldOutBehavior || 'show';
    this.hideMainProduct = boot.hideMainProduct === true;
    this.pricingMode = boot.pricingMode || 'fixed';
    this.products = Array.isArray(boot.products) ? boot.products : [];
    this.trays = [];
    this.selectionsByTray = Object.create(null);
    this.activeTrayId = null;
    this.activeSlot = '1';
    this.pendingCard = null;
    this.lastFocused = null;
    this.destroyed = false;
    this._textureCache = Object.create(null); // url -> Promise<img>
    this._textureErrors = Object.create(null); // url -> true
    this._textureLoaded = Object.create(null); // url -> true after verified decode
    this._qty = 1;
    this._listeners = [];
    this._submitting = false;

    this.modal = root.querySelector('[data-modal]');
    this.grid = this.modal.querySelector('[data-grid]');
    this.trayMount = this.modal.querySelector('[data-tray-mount]');
    this.summaryMount = this.modal.querySelector('[data-slot-summary]');
    this.trayPicker = this.modal.querySelector('[data-tray-picker]');
    this.diagEl = this.modal.querySelector('[data-layout-diag]');

    this._bindUiRefs();
    this._applyPageActiveClass();
    this._buildTrayRegistry(boot.trays || []);
    this._buildCatalogDom();
    this._bindEvents();
    this._selectInitialTray();
    activeEngines.push(this);
    this.render();
  }

  WgnSmartTrayEngine.prototype._applyPageActiveClass = function () {
    if (this.hideMainProduct) {
      document.body.classList.add('wgn-stc-page-active');
      this.root.setAttribute('data-hide-main-product', '1');
    } else {
      this.root.removeAttribute('data-hide-main-product');
      this._syncPageActiveClass();
    }
  };

  WgnSmartTrayEngine.prototype._syncPageActiveClass = function () {
    var stillNeeded = activeEngines.some(function (eng) {
      return !eng.destroyed && eng.hideMainProduct;
    });
    if (!stillNeeded) document.body.classList.remove('wgn-stc-page-active');
    else document.body.classList.add('wgn-stc-page-active');
  };

  WgnSmartTrayEngine.prototype._on = function (el, evt, fn, opts) {
    if (!el) return;
    el.addEventListener(evt, fn, opts);
    this._listeners.push({ el: el, evt: evt, fn: fn, opts: opts });
  };

  WgnSmartTrayEngine.prototype.destroy = function () {
    this.destroyed = true;
    this._listeners.forEach(function (l) {
      l.el.removeEventListener(l.evt, l.fn, l.opts);
    });
    this._listeners = [];
    this.root.removeAttribute('data-wgn-initialized');
    this.root.removeAttribute('data-hide-main-product');
    var idx = activeEngines.indexOf(this);
    if (idx >= 0) activeEngines.splice(idx, 1);
    this._syncPageActiveClass();
  };

  WgnSmartTrayEngine.prototype.ensureImageLoaded = function (url) {
    var self = this;
    if (!url) return Promise.reject(new Error('Missing image URL'));
    if (this._textureLoaded[url]) return Promise.resolve(true);
    if (this._textureErrors[url]) return Promise.reject(new Error('Previously failed: ' + url));
    if (!this._textureCache[url]) {
      this._textureCache[url] = loadImage(url).then(function (img) {
        self._textureLoaded[url] = true;
        delete self._textureErrors[url];
        return img;
      }).catch(function (err) {
        self._textureErrors[url] = true;
        delete self._textureCache[url];
        throw err;
      });
    }
    return this._textureCache[url];
  };

  WgnSmartTrayEngine.prototype.retryTexture = function (url, slotIdx) {
    if (!url) return;
    delete this._textureErrors[url];
    delete this._textureLoaded[url];
    delete this._textureCache[url];
    var sel = this.getSel()[String(slotIdx)];
    if (sel && sel.texture === url) {
      sel.textureOk = false;
      sel.textureLoading = true;
      sel.textureError = false;
    }
    var self = this;
    this.ensureImageLoaded(url).then(function () {
      if (sel) {
        sel.textureOk = true;
        sel.textureLoading = false;
        sel.textureError = false;
      }
      self._setHint('Section ' + slotIdx + ' texture loaded successfully.', 'ok');
      self.render();
    }).catch(function () {
      if (sel) {
        sel.textureOk = false;
        sel.textureLoading = false;
        sel.textureError = true;
      }
      self._setHint('Section ' + slotIdx + ' texture still failed to load. Replace the item or retry.', 'error');
      self.render();
    });
  };

  WgnSmartTrayEngine.prototype._trayIsPurchasable = function (tray) {
    return !!(tray &&
      tray.layoutOk &&
      tray.baseImage &&
      tray.baseImageOk === true &&
      tray.variantId &&
      tray.available &&
      tray.layout &&
      tray.layout.slots.length > 0);
  };

  WgnSmartTrayEngine.prototype._bindUiRefs = function () {
    var m = this.modal;
    this.openBtn = this.root.querySelector('[data-open]');
    this.closeBtn = m.querySelector('[data-close]');
    this.hint = m.querySelector('[data-hint]');
    this.priceEl = m.querySelector('[data-total-price]');
    this.atcBtn = m.querySelector('[data-atc]');
    this.atcText = m.querySelector('[data-atc-text]');
    this.atcSubtext = m.querySelector('[data-atc-subtext]');
    this.qtyIn = m.querySelector('[data-qty]');
    this.inc = m.querySelector('[data-inc]');
    this.dec = m.querySelector('[data-dec]');
    this.searchInputs = m.querySelectorAll('[data-search]');
    this.progressBar = m.querySelector('[data-progress-bar]');
    this.progressBadge = m.querySelector('[data-progress-badge]');
    this.progressCountText = m.querySelector('[data-progress-count]');
    this.progressRemText = m.querySelector('[data-progress-remaining]');
    this.srStatus = m.querySelector('[data-sr-status]');
    this.contextBanner = m.querySelector('[data-context-banner]');
    this.contextSlotBadge = m.querySelector('[data-context-slot]');
    this.contextText = m.querySelector('[data-context-text]');
    this.contextClearBtn = m.querySelector('[data-context-clear]');
    this.catalogCountEl = m.querySelector('[data-catalog-count]');
    this.sortSelect = m.querySelector('[data-sort-select]');
    this.clearFiltersBtn = m.querySelector('[data-clear-filters]');
    this.mobileBackBtn = m.querySelector('[data-mobile-back]');
    this.mobileClearBtn = m.querySelector('[data-mobile-clear]');
    this.mobileCatSelect = m.querySelector('[data-mobile-cats]');
    this.mobileTitle = m.querySelector('[data-mobile-title]');
    this.mobileViewTrayBtn = m.querySelector('[data-mobile-view-tray]');
    this.mobileStickyActiveText = m.querySelector('[data-mobile-sticky-active]');
    this.tabletCatSelect = m.querySelector('[data-tablet-cats]');
    this.tabletTagsToggle = m.querySelector('[data-tablet-tags-toggle]');
    this.tabletTagsPanel = m.querySelector('[data-tablet-tags-panel]');
    this.tabletTagsBadge = m.querySelector('[data-tablet-tags-count]');
    this.tabletClearBtn = m.querySelector('[data-tablet-clear]');
    this.tagsToggle = m.querySelector('[data-tags-toggle]');
    this.tagsPanel = m.querySelector('[data-tags-panel]');
    this.tagsBadge = m.querySelector('[data-tags-count]');
    this.activeCat = null;
    this.activeTags = new Set();
    this.activeSort = 'default';
  };

  WgnSmartTrayEngine.prototype._buildTrayRegistry = function (trayConfigs) {
    var self = this;
    var idSeen = Object.create(null);
    this.trays = [];

    (trayConfigs || []).forEach(function (t) {
      if (!t || t.enabled === false) return;
      var id = String(t.id || '').trim();
      if (!id) {
        console.warn('[WgnSmartTray] Skipping tray with empty id', t);
        return;
      }
      if (idSeen[id]) {
        console.warn('[WgnSmartTray] Duplicate tray id skipped:', id);
        return;
      }
      idSeen[id] = true;

      var parsed = parseAndValidateTrayLayout(t.layoutJson || '');
      var baseImage = t.baseImage || '';
      var layoutErrors = (parsed.errors || []).slice();
      if (!baseImage) layoutErrors.push('Missing tray base image.');
      var entry = {
        id: id,
        name: t.name || id,
        subtitle: t.subtitle || '',
        baseImage: baseImage,
        baseImageOk: false,
        baseImageLoading: !!baseImage,
        baseImageError: !baseImage,
        variantId: t.variantId ? Number(t.variantId) : 0,
        priceCents: t.priceCents != null ? Number(t.priceCents) : 0,
        available: t.available !== false,
        layout: parsed.ok ? parsed.layout : null,
        layoutOk: parsed.ok && !!baseImage,
        layoutErrors: layoutErrors,
        layoutWarnings: parsed.warnings || [],
        sortOrder: Number(t.sortOrder) || 0
      };
      // Geometry may parse OK but tray still not purchasable until base image verifies
      if (parsed.ok && !baseImage) entry.layoutOk = false;
      self.trays.push(entry);
      if (!self.selectionsByTray[id]) self.selectionsByTray[id] = Object.create(null);

      if (baseImage) {
        (function (trayEntry) {
          self.ensureImageLoaded(trayEntry.baseImage).then(function () {
            trayEntry.baseImageOk = true;
            trayEntry.baseImageLoading = false;
            trayEntry.baseImageError = false;
            if (parsed.ok) trayEntry.layoutOk = true;
            self._renderTrayPicker();
            self._showDiag(self.getActiveTray());
            self.validateATC();
            self.render();
          }).catch(function () {
            trayEntry.baseImageOk = false;
            trayEntry.baseImageLoading = false;
            trayEntry.baseImageError = true;
            trayEntry.layoutOk = false;
            if (trayEntry.layoutErrors.indexOf('Tray base image failed to load.') === -1) {
              trayEntry.layoutErrors.push('Tray base image failed to load.');
            }
            self._renderTrayPicker();
            self._showDiag(self.getActiveTray());
            self.validateATC();
            self.render();
          });
        })(entry);
      }
    });

    this.trays.sort(function (a, b) { return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name); });
    this._renderTrayPicker();
  };

  WgnSmartTrayEngine.prototype._renderTrayPicker = function () {
    var wrap = this.trayPicker;
    if (!wrap) return;
    wrap.innerHTML = '';
    var self = this;
    var name = 'wgn-tray-' + this.sectionId;

    if (!this.trays.length) {
      wrap.innerHTML = '<p class="wgn-stc-tray-empty">No tray definitions configured. Add Tray Definition blocks in the Theme Editor.</p>';
      return;
    }

    this.trays.forEach(function (tray, idx) {
      var rowId = 'wgn-sz-' + self.sectionId + '-' + idx;
      var row = document.createElement('div');
      row.className = 'size-row' + (idx === 0 ? ' is-selected' : '');
      row.setAttribute('data-tray-row', tray.id);

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = tray.id;
      input.id = rowId;
      input.checked = idx === 0;
      input.setAttribute('data-tray-radio', tray.id);
      input.setAttribute('data-variant', String(tray.variantId || ''));
      input.setAttribute('data-price-cents', String(tray.priceCents || 0));
      input.disabled = false;

      var icon = document.createElement('span');
      icon.className = 'size-row-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg class="tray-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
          '<polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"></polygon>' +
          '<line x1="12" y1="22" x2="12" y2="15.5"></line>' +
          '<polyline points="22 8.5 12 15.5 2 8.5"></polyline>' +
        '</svg>';

      var label = document.createElement('label');
      label.setAttribute('for', rowId);
      var strong = document.createElement('strong');
      strong.textContent = tray.name;
      var small = document.createElement('small');
      if (!tray.baseImage) small.textContent = 'Missing base image';
      else if (tray.baseImageLoading) small.textContent = 'Verifying base image…';
      else if (tray.baseImageError) small.textContent = 'Base image failed to load';
      else if (!tray.layoutOk) small.textContent = 'Invalid layout JSON';
      else if (!tray.variantId) small.textContent = 'Missing Shopify product';
      else if (!tray.available) small.textContent = 'Unavailable';
      else small.textContent = tray.subtitle || (tray.layout.slots.length + ' sections');
      label.appendChild(strong);
      label.appendChild(small);

      var price = document.createElement('div');
      price.className = 'size-price';
      price.textContent = tray.priceCents > 0 ? moneyFormat(tray.priceCents, self.currency) : '';

      row.appendChild(input);
      row.appendChild(icon);
      row.appendChild(label);
      row.appendChild(price);
      wrap.appendChild(row);

      self._on(input, 'change', function () {
        self._switchTray(tray.id);
      });
      self._on(row, 'click', function (e) {
        if (e.target === input) return;
        if (!input.checked) {
          input.checked = true;
          self._switchTray(tray.id);
        }
      });
    });
  };

  WgnSmartTrayEngine.prototype._selectInitialTray = function () {
    var first = this.trays.find(function (t) {
      return t.layout && t.baseImage && t.variantId && t.available && (t.layoutOk || t.baseImageLoading);
    }) || this.trays[0];
    if (first) this._switchTray(first.id, true);
  };

  WgnSmartTrayEngine.prototype.getActiveTray = function () {
    var id = this.activeTrayId;
    for (var i = 0; i < this.trays.length; i++) {
      if (this.trays[i].id === id) return this.trays[i];
    }
    return null;
  };

  WgnSmartTrayEngine.prototype.getRequiredSlots = function () {
    var tray = this.getActiveTray();
    return tray && tray.layout ? tray.layout.slots.length : 0;
  };

  WgnSmartTrayEngine.prototype.getSel = function () {
    var id = this.activeTrayId;
    if (!id) return Object.create(null);
    if (!this.selectionsByTray[id]) this.selectionsByTray[id] = Object.create(null);
    return this.selectionsByTray[id];
  };

  /**
   * Replace any prior tray's success/progress toast with the CURRENT tray name + fill count.
   * Selections stay in selectionsByTray — this only refreshes messaging/UI copy.
   */
  WgnSmartTrayEngine.prototype.updateHintForCurrentTray = function () {
    var tray = this.getActiveTray();
    var name = (tray && tray.name) ? tray.name : 'Tray';
    var filled = this.countFilled();
    var req = this.getRequiredSlots() || 0;
    var rem = Math.max(0, req - filled);
    if (!tray) {
      this._setHint('Select a tray to begin.', 'info');
      return;
    }
    if (filled === 0) {
      this._setHint(
        name + ' selected — 0 of ' + req + ' sections filled. Click a tray section or select an item to begin.',
        'info'
      );
    } else if (req > 0 && filled >= req) {
      this._setHint(
        name + ' selected — ' + filled + ' of ' + req + ' sections filled. Your tray is complete!',
        'ok'
      );
    } else {
      this._setHint(
        name + ' selected — ' + filled + ' of ' + req + ' sections filled (' + rem + ' remaining).',
        'info'
      );
    }
  };

  WgnSmartTrayEngine.prototype._switchTray = function (trayId, silent) {
    this.activeTrayId = trayId;
    var tray = this.getActiveTray();
    this._pruneSelectionsToLayout(tray);
    this.activeSlot = tray && tray.layout && tray.layout.slots[0] ? String(tray.layout.slots[0].number) : '1';

    if (this.trayPicker) {
      this.trayPicker.querySelectorAll('.size-row').forEach(function (row) {
        var inp = row.querySelector('input[type="radio"]');
        row.classList.toggle('is-selected', !!(inp && inp.checked));
      });
    }

    this._renderTraySvg();
    this._renderSummaryScaffold();
    this._showDiag(tray);
    if (!silent) {
      this.render();
      // Always overwrite prior tray success toasts with current-tray status.
      this.updateHintForCurrentTray();
    }
  };

  WgnSmartTrayEngine.prototype._pruneSelectionsToLayout = function (tray) {
    if (!tray || !tray.layout) return;
    var sel = this.selectionsByTray[tray.id] || Object.create(null);
    var valid = Object.create(null);
    tray.layout.slots.forEach(function (s) { valid[String(s.number)] = true; });
    Object.keys(sel).forEach(function (k) {
      if (!valid[k]) delete sel[k];
    });
  };

  WgnSmartTrayEngine.prototype._showDiag = function (tray) {
    if (!this.diagEl) return;
    if (!tray) {
      this.diagEl.hidden = true;
      return;
    }
    if (tray.baseImageLoading) {
      this.diagEl.hidden = false;
      this.diagEl.className = 'wgn-stc-diag is-warn';
      this.diagEl.textContent = 'Verifying tray base image… purchasing stays disabled until it loads.';
      return;
    }
    if (this._trayIsPurchasable(tray)) {
      var warn = (tray.layoutWarnings || []).join(' ');
      if (warn && this.root.hasAttribute('data-design-mode')) {
        this.diagEl.hidden = false;
        this.diagEl.textContent = 'Layout OK (' + tray.layout.slots.length + ' slots). Notes: ' + warn;
        this.diagEl.className = 'wgn-stc-diag is-warn';
      } else {
        this.diagEl.hidden = true;
      }
      return;
    }
    this.diagEl.hidden = false;
    this.diagEl.className = 'wgn-stc-diag is-error';
    var msgs = (tray.layoutErrors || []).slice();
    if (!tray.variantId) msgs.push('Assign a purchasable Shopify tray product.');
    if (tray.variantId && !tray.available) msgs.push('Tray product is sold out / unavailable.');
    this.diagEl.textContent = 'Tray not purchasable — ' + (msgs.join(' ') || 'Fix configuration in Theme Editor.');
  };

  WgnSmartTrayEngine.prototype._renderTraySvg = function () {
    var mount = this.trayMount;
    if (!mount) return;
    mount.innerHTML = '';
    var tray = this.getActiveTray();
    if (!tray || !tray.layout) {
      mount.innerHTML = '<div class="wgn-stc-tray-error">Tray geometry unavailable. Fix layout JSON in Theme Editor.</div>';
      return;
    }

    var vb = tray.layout.viewBox;
    var sid = this.sectionId;
    var svg = createEl('svg', {
      class: 'tray-svg',
      viewBox: '0 0 ' + vb.width + ' ' + vb.height,
      xmlns: svgNs(),
      'aria-label': 'Tray layout (' + tray.name + ')'
    });

    var defs = createEl('defs');
    tray.layout.slots.forEach(function (slot) {
      var clipId = 'wgn-clip-' + sid + '-' + tray.id + '-' + slot.number;
      var clip = createEl('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
      clip.appendChild(createEl('polygon', { points: pointsAttr(slot.points) }));
      defs.appendChild(clip);
    });
    svg.appendChild(defs);

    if (tray.baseImage) {
      svg.appendChild(createEl('image', {
        href: tray.baseImage,
        x: '0', y: '0',
        width: String(vb.width),
        height: String(vb.height),
        preserveAspectRatio: 'xMidYMid meet'
      }));
    }

    var fillsG = createEl('g', { class: 'slot-fills', 'data-fills-layer': '1' });
    tray.layout.slots.forEach(function (slot) {
      var clipId = 'wgn-clip-' + sid + '-' + tray.id + '-' + slot.number;
      var img = createEl('image', {
        class: 'slot-fill',
        'data-fill': String(slot.number),
        x: '0', y: '0',
        width: String(vb.width),
        height: String(vb.height),
        preserveAspectRatio: 'xMidYMid slice',
        opacity: '0',
        'clip-path': 'url(#' + clipId + ')'
      });
      fillsG.appendChild(img);
    });
    svg.appendChild(fillsG);

    var hitG = createEl('g', { class: 'slot-hits' });
    tray.layout.slots.forEach(function (slot) {
      hitG.appendChild(createEl('polygon', {
        class: 'tray-sec',
        'data-slot': String(slot.number),
        points: pointsAttr(slot.points)
      }));
    });
    svg.appendChild(hitG);

    var markersG = createEl('g', { class: 'slot-marker-group' });
    tray.layout.slots.forEach(function (slot) {
      var c = centroid(slot.points);
      var g = createEl('g', {
        class: 'slot-marker',
        'data-marker': String(slot.number),
        transform: 'translate(' + c.x + ', ' + c.y + ')'
      });
      g.appendChild(createEl('circle', { r: '28', class: 'slot-marker-circle' }));
      var text = createEl('text', {
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
        class: 'slot-marker-text'
      });
      text.textContent = String(slot.number);
      g.appendChild(text);
      markersG.appendChild(g);
    });
    svg.appendChild(markersG);

    mount.appendChild(svg);
  };

  WgnSmartTrayEngine.prototype._renderSummaryScaffold = function () {
    var mount = this.summaryMount;
    if (!mount) return;
    mount.innerHTML = '';
    var tray = this.getActiveTray();
    if (!tray || !tray.layout) return;

    tray.layout.slots.forEach(function (slot) {
      var k = String(slot.number);
      var row = document.createElement('li');
      row.className = 'slot-summary-row';
      row.setAttribute('data-slot-row', k);

      /* Match legacy CSS grid: 24px badge | main | actions (approved right-panel rows) */
      row.innerHTML =
        '<span class="slot-row-badge" aria-hidden="true">' + k + '</span>' +
        '<div class="slot-row-main" data-row-main="' + k + '">' +
          '<img class="slot-row-thumb" data-row-thumb="' + k + '" alt="" width="60" height="60" loading="lazy">' +
          '<span class="slot-row-title is-empty" data-row-title="' + k + '">Empty section</span>' +
        '</div>' +
        '<div class="slot-row-actions">' +
          '<button type="button" class="slot-btn slot-btn--add" data-row-btn-add="' + k + '" aria-label="Section ' + k + ': Empty. Select to add item">+ Add</button>' +
          '<button type="button" class="slot-btn slot-btn--change" data-row-btn-change="' + k + '" aria-label="Section ' + k + ': Change item" style="display:none">Change</button>' +
          '<button type="button" class="slot-btn slot-btn--remove" data-row-btn-remove="' + k + '" aria-label="Section ' + k + ': Remove item" style="display:none" title="Remove item">×</button>' +
        '</div>';
      mount.appendChild(row);
    });
  };

  WgnSmartTrayEngine.prototype._buildCatalogDom = function () {
    var grid = this.grid;
    if (!grid) return;
    grid.innerHTML = '';
    var self = this;
    var soldList = (this.root.getAttribute('data-manual-sold-out') || '')
      .split(/\n|<br\s*\/?>/i)
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);

    this.products.forEach(function (p, idx) {
      if (!p || !p.title || p.title === 'Error fixer') return;
      if (!p.cardImage) return;

      var sold = !!p.soldOut;
      if (!sold && soldList.indexOf(String(p.title).trim().toLowerCase()) !== -1) sold = true;
      if (sold && self.soldOutBehavior === 'hide') return;

      var hasTexture = !!(p.masterTexture);
      var card = document.createElement('div');
      card.className = 'card' + (sold ? ' sold-out' : '') + (!hasTexture && !sold ? ' is-unavailable' : '');
      card.setAttribute('data-card', '');
      card.setAttribute('data-title', p.title);
      card.setAttribute('data-cat', p.category || '');
      card.setAttribute('data-tags', p.tags || '');
      card.setAttribute('data-variant-id', p.variantId || '');
      card.setAttribute('data-card-price', p.priceCents || 0);
      card.setAttribute('data-thumb', p.cardImage);
      card.setAttribute('data-master-texture', p.masterTexture || '');
      card.setAttribute('data-has-texture', hasTexture ? '1' : '0');
      card.setAttribute('data-original-index', String(idx));
      card.tabIndex = 0;

      card.innerHTML =
        '<div class="card-img-wrap">' +
          '<img src="' + p.cardImage + '" alt="" width="600" height="600" loading="lazy" decoding="async">' +
          (sold && self.soldOutBehavior === 'show' ? '<div class="sold-out-badge">Sold Out</div>' : '') +
          (!hasTexture && !sold ? '<div class="sold-out-badge">No texture</div>' : '') +
        '</div>' +
        (p.tags ? '<div class="card-tags-row">' + p.tags + '</div>' : '') +
        '<div class="name"></div>' +
        '<div class="card-tray-badge" data-card-tray-badge>' +
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>' +
          '<span class="card-tray-badge-text">In Tray</span>' +
        '</div>' +
        '<button type="button" class="card-action-btn" data-card-btn>Add to Tray</button>';
      card.querySelector('.name').textContent = p.title;
      card.querySelector('img').alt = p.title;
      grid.appendChild(card);
    });
  };

  WgnSmartTrayEngine.prototype.isMobile = function () {
    return window.innerWidth < 768;
  };

  WgnSmartTrayEngine.prototype.setView = function (view) {
    this.modal.setAttribute('data-view', view);
  };

  WgnSmartTrayEngine.prototype.openModal = function () {
    this.lastFocused = document.activeElement;
    this._prevScrollY = window.scrollY || 0;
    this.modal.classList.add('open');
    document.documentElement.classList.add('wgn-stc-open');
    document.body.classList.add('wgn-stc-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (this.isMobile()) this.setView('panel');
    else this.modal.removeAttribute('data-view');
    this.applyFilters();
    this.render();
  };

  WgnSmartTrayEngine.prototype.closeModal = function () {
    this.modal.classList.remove('open');
    document.documentElement.classList.remove('wgn-stc-open');
    document.body.classList.remove('wgn-stc-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (this._prevScrollY) window.scrollTo(0, this._prevScrollY);
    if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
  };

  WgnSmartTrayEngine.prototype.countFilled = function () {
    var sel = this.getSel();
    return Object.keys(sel).filter(function (k) {
      var s = sel[k];
      return !!(s && s.textureOk && !s.textureError && !s.textureLoading);
    }).length;
  };

  WgnSmartTrayEngine.prototype.countOccupied = function () {
    return Object.keys(this.getSel()).filter(function (k) {
      return !!this.getSel()[k];
    }, this).length;
  };

  WgnSmartTrayEngine.prototype.findNextEmptySlot = function () {
    var tray = this.getActiveTray();
    if (!tray || !tray.layout) return null;
    var sel = this.getSel();
    for (var i = 0; i < tray.layout.slots.length; i++) {
      var k = String(tray.layout.slots[i].number);
      var s = sel[k];
      if (!s || s.textureError) return k;
    }
    return null;
  };

  WgnSmartTrayEngine.prototype.setActiveSlot = function (slotIdx) {
    this.activeSlot = slotIdx ? String(slotIdx) : null;
    this.renderActiveSlotUI();
  };

  WgnSmartTrayEngine.prototype.place = function (card, slotIdx) {
    var self = this;
    var texture = card.getAttribute('data-master-texture');
    var thumb = card.getAttribute('data-thumb') || '';
    if (!texture) {
      this._setHint('This item has no master texture configured yet. Choose another item or ask the store to upload textures.', 'error');
      return;
    }
    // Never use catalog thumbnail as a fake tray texture
    if (thumb && texture === thumb) {
      // Allowed only if merchant intentionally used same file for both; still must decode as texture
    }

    var tray = this.getActiveTray();
    if (!tray || !tray.layout || !tray.layoutOk || !tray.baseImageOk) {
      this._setHint('This tray is not ready (invalid layout or base image). Purchasing is disabled until configuration is fixed.', 'error');
      return;
    }

    var key = String(slotIdx);
    this.getSel()[key] = {
      title: card.getAttribute('data-title'),
      variantId: card.getAttribute('data-variant-id') ? Number(card.getAttribute('data-variant-id')) : null,
      price: Number(card.getAttribute('data-card-price') || 0),
      texture: texture,
      thumb: thumb || '',
      textureOk: false,
      textureLoading: true,
      textureError: false
    };

    this._setHint('Loading texture for Section ' + slotIdx + '…', 'info');
    if (this.isMobile()) this.setView('panel');
    this.render();

    this.ensureImageLoaded(texture).then(function () {
      var cur = self.getSel()[key];
      if (!cur || cur.texture !== texture) return; // replaced meanwhile
      cur.textureOk = true;
      cur.textureLoading = false;
      cur.textureError = false;
      var next = self.findNextEmptySlot();
      if (next) {
        self.setActiveSlot(next);
        self._setHint('Added ' + cur.title + ' to Section ' + slotIdx + '! Choose an item for Section ' + next + '.', 'ok');
      } else {
        self.setActiveSlot(null);
        self._setHint('Added ' + cur.title + ' to Section ' + slotIdx + '! Your tray is complete!', 'ok');
      }
      self.render();
    }).catch(function () {
      var cur = self.getSel()[key];
      if (!cur || cur.texture !== texture) return;
      cur.textureOk = false;
      cur.textureLoading = false;
      cur.textureError = true;
      self.setActiveSlot(key);
      self._setHint('Section ' + slotIdx + ' (' + cur.title + '): master texture failed to load. Retry or choose a different item.', 'error');
      self.render();
    });
  };

  WgnSmartTrayEngine.prototype.removeSlot = function (slotIdx) {
    delete this.getSel()[String(slotIdx)];
    this.setActiveSlot(String(slotIdx));
    this._setHint('Removed item from Section ' + slotIdx + '. Choose an item to fill it.', 'ok');
    this.render();
  };

  WgnSmartTrayEngine.prototype._setHint = function (msg, kind) {
    if (!this.hint) return;
    this.hint.textContent = msg;
    if (kind === 'error') {
      this.hint.style.background = '#fef2f2';
      this.hint.style.borderColor = '#fca5a5';
      this.hint.style.color = '#b91c1c';
    } else if (kind === 'info') {
      this.hint.style.background = '#dbeafe';
      this.hint.style.borderColor = '#3b82f6';
      this.hint.style.color = '#1e40af';
    } else {
      this.hint.style.background = '#f0fdf4';
      this.hint.style.borderColor = '#bbf7d0';
      this.hint.style.color = '#166534';
    }
  };

  WgnSmartTrayEngine.prototype.renderActiveSlotUI = function () {
    var mount = this.trayMount;
    if (mount) {
      mount.querySelectorAll('.tray-sec').forEach(function (p) {
        p.classList.toggle('active', p.getAttribute('data-slot') === this.activeSlot);
      }, this);
      mount.querySelectorAll('.slot-marker').forEach(function (m) {
        m.classList.toggle('is-active', m.getAttribute('data-marker') === this.activeSlot);
      }, this);
    }
    if (this.summaryMount) {
      this.summaryMount.querySelectorAll('.slot-summary-row').forEach(function (row) {
        row.classList.toggle('is-active', row.getAttribute('data-slot-row') === this.activeSlot);
      }, this);
    }
    if (this.contextBanner) {
      if (this.activeSlot) {
        this.contextBanner.style.display = 'flex';
        if (this.contextSlotBadge) this.contextSlotBadge.textContent = 'Section ' + this.activeSlot;
        var curr = this.getSel()[this.activeSlot];
        if (this.contextText) {
          this.contextText.textContent = curr
            ? 'Replacing Section ' + this.activeSlot + ': ' + curr.title
            : 'Choose an item for Section ' + this.activeSlot;
        }
      } else {
        this.contextBanner.style.display = 'none';
      }
    }
    this.updateCardContextButtons();
  };

  WgnSmartTrayEngine.prototype.updateCardContextButtons = function () {
    var self = this;
    if (!this.grid) return;
    this.grid.querySelectorAll('[data-card]').forEach(function (card) {
      var btn = card.querySelector('[data-card-btn]');
      if (!btn) return;
      if (card.classList.contains('sold-out')) {
        btn.textContent = 'Sold Out';
        btn.disabled = true;
        return;
      }
      var has = card.getAttribute('data-has-texture') === '1';
      card.classList.toggle('is-unavailable', !has);
      if (!has) {
        btn.textContent = 'Texture required';
        btn.disabled = true;
        return;
      }
      btn.disabled = false;
      if (self.activeSlot) {
        var curr = self.getSel()[self.activeSlot];
        btn.textContent = curr ? 'Replace Section ' + self.activeSlot : 'Add to Section ' + self.activeSlot;
      } else {
        var next = self.findNextEmptySlot() || '1';
        btn.textContent = 'Add to Section ' + next;
      }
    });
  };

  WgnSmartTrayEngine.prototype.render = function () {
    var tray = this.getActiveTray();
    var sel = this.getSel();
    var req = this.getRequiredSlots();
    var mount = this.trayMount;

    // Card badges
    var titleCounts = {};
    Object.keys(sel).forEach(function (k) {
      var s = sel[k];
      if (s && s.title) titleCounts[s.title] = (titleCounts[s.title] || 0) + 1;
    });
    if (this.grid) {
      this.grid.querySelectorAll('[data-card]').forEach(function (card) {
        var title = card.getAttribute('data-title');
        var count = titleCounts[title] || 0;
        card.classList.toggle('sticky', count > 0);
        var badge = card.querySelector('[data-card-tray-badge] .card-tray-badge-text');
        if (badge) badge.textContent = count > 1 ? 'In Tray (×' + count + ')' : 'In Tray';
      });
    }

    // SVG fills — only after verified texture decode (never card thumbnail)
    if (mount && tray && tray.layout) {
      tray.layout.slots.forEach(function (slot) {
        var k = String(slot.number);
        var s = sel[k];
        var img = mount.querySelector('.slot-fill[data-fill="' + k + '"]');
        var poly = mount.querySelector('.tray-sec[data-slot="' + k + '"]');
        var marker = mount.querySelector('.slot-marker[data-marker="' + k + '"]');
        if (s && s.texture && s.textureOk) {
          if (img) {
            img.setAttribute('href', s.texture);
            img.setAttribute('opacity', '1');
          }
          if (poly) { poly.classList.add('filled'); poly.classList.remove('is-error'); }
          if (marker) { marker.classList.add('is-filled'); marker.classList.remove('is-error'); }
        } else {
          if (img) {
            img.removeAttribute('href');
            img.setAttribute('opacity', '0');
          }
          if (poly) {
            poly.classList.remove('filled');
            poly.classList.toggle('is-error', !!(s && s.textureError));
          }
          if (marker) {
            marker.classList.remove('is-filled');
            marker.classList.toggle('is-error', !!(s && s.textureError));
          }
        }
      });
    }

    // Summary rows
    if (this.summaryMount && tray && tray.layout) {
      tray.layout.slots.forEach(function (slot) {
        var k = String(slot.number);
        var s = sel[k];
        var row = this.summaryMount.querySelector('.slot-summary-row[data-slot-row="' + k + '"]');
        if (!row) return;
        var titleEl = row.querySelector('[data-row-title="' + k + '"]');
        var thumbEl = row.querySelector('[data-row-thumb="' + k + '"]');
        var btnAdd = row.querySelector('[data-row-btn-add="' + k + '"]');
        var btnChange = row.querySelector('[data-row-btn-change="' + k + '"]');
        var btnRemove = row.querySelector('[data-row-btn-remove="' + k + '"]');
      var btnRetry = row.querySelector('[data-row-btn-retry="' + k + '"]');
        if (!btnRetry) {
          var actions = row.querySelector('.slot-row-actions');
          if (actions) {
            btnRetry = document.createElement('button');
            btnRetry.type = 'button';
            btnRetry.className = 'slot-btn';
            btnRetry.setAttribute('data-row-btn-retry', k);
            btnRetry.textContent = 'Retry';
            btnRetry.style.display = 'none';
            actions.appendChild(btnRetry);
          }
        }
        if (s && s.title) {
          row.classList.add('is-filled');
          row.classList.toggle('is-error', !!s.textureError);
          row.classList.toggle('is-loading', !!s.textureLoading);
          if (titleEl) {
            var label = s.title;
            if (s.textureLoading) label += ' (loading…)';
            if (s.textureError) label += ' (texture failed)';
            titleEl.textContent = label;
            titleEl.classList.remove('is-empty');
          }
          if (thumbEl) {
            if (s.thumb) {
              thumbEl.src = s.thumb;
              thumbEl.alt = s.title;
            } else {
              thumbEl.removeAttribute('src');
              thumbEl.alt = '';
            }
          }
          if (btnAdd) btnAdd.style.display = 'none';
          if (btnChange) btnChange.style.display = '';
          if (btnRemove) btnRemove.style.display = '';
          if (btnRetry) btnRetry.style.display = s.textureError ? '' : 'none';
        } else {
          row.classList.remove('is-filled', 'is-error', 'is-loading');
          if (titleEl) { titleEl.textContent = 'Empty section'; titleEl.classList.add('is-empty'); }
          if (thumbEl) {
            thumbEl.removeAttribute('src');
            thumbEl.alt = '';
          }
          if (btnAdd) btnAdd.style.display = '';
          if (btnChange) btnChange.style.display = 'none';
          if (btnRemove) btnRemove.style.display = 'none';
          if (btnRetry) btnRetry.style.display = 'none';
        }
      }, this);
    }

    var filled = this.countFilled();
    var percent = req ? Math.min(100, Math.round((filled / req) * 100)) : 0;
    if (this.progressBar) {
      this.progressBar.style.width = percent + '%';
      this.progressBar.classList.toggle('is-complete', filled >= req && req > 0);
    }
    if (this.progressBadge) {
      var done = filled >= req && req > 0;
      this.progressBadge.textContent = done ? 'Complete' : 'In Progress';
      this.progressBadge.classList.toggle('is-complete', done);
    }
    if (this.progressCountText) this.progressCountText.textContent = filled + ' of ' + req + ' sections filled';
    if (this.progressRemText) {
      var rem = Math.max(0, req - filled);
      this.progressRemText.textContent = rem === 0 && req > 0 ? 'Ready to add' : rem + ' remaining';
    }
    if (this.srStatus) this.srStatus.textContent = filled + ' of ' + req + ' sections filled';
    this.modal.querySelectorAll('[data-filled-count], [data-filled-count-mobile]').forEach(function (el) {
      el.textContent = String(filled);
    });
    this.modal.querySelectorAll('[data-required-slots]').forEach(function (el) {
      el.textContent = String(req);
    });

    this.renderActiveSlotUI();
    this.validateATC();
    this.updatePrice();
    this.applyFilters();
  };

  /**
   * Pure business readiness — never consult atcBtn.disabled or _submitting.
   * UI disable state is a separate concern (duplicate-click guard vs invalid tray).
   */
  WgnSmartTrayEngine.prototype.getCheckoutReadiness = function () {
    var tray = this.getActiveTray();
    var filled = this.countFilled();
    var req = this.getRequiredSlots();
    var sel = this.getSel();
    var reasons = [];
    var failedSlots = [];
    var loadingSlots = [];
    var missingTextureSlots = [];
    var missingSlots = [];

    if (typeof document !== 'undefined' && document.body &&
        (document.body.classList.contains('wgn-vacation-atc-disabled') ||
         document.body.getAttribute('data-wgn-vacation-add-blocked') === 'true')) {
      reasons.push('Ordering paused by vacation mode.');
    }

    if (!tray) {
      reasons.push('Missing valid tray definition.');
    } else {
      if (!tray.baseImage) reasons.push('Missing base image.');
      else if (tray.baseImageLoading) reasons.push('Base image still loading.');
      else if (tray.baseImageError || tray.baseImageOk !== true) reasons.push('Base image load failure.');

      if (!tray.layoutOk || !tray.layout || !tray.layout.slots || !tray.layout.slots.length) {
        reasons.push('Invalid polygon layout.');
      }

      if (!tray.variantId) reasons.push('Missing parent product variant.');
      else if (!tray.available) reasons.push('Parent product unavailable.');
    }

    if (!(req > 0)) {
      reasons.push('Invalid polygon layout.');
    } else if (filled < req) {
      reasons.push('Incomplete slots.');
      if (tray && tray.layout && tray.layout.slots) {
        tray.layout.slots.forEach(function (slot) {
          if (!sel[String(slot.number)]) missingSlots.push(String(slot.number));
        });
      }
    }

    if (tray && tray.layout && tray.layout.slots) {
      tray.layout.slots.forEach(function (slot) {
        var key = String(slot.number);
        var s = sel[key];
        if (!s) return;
        if (!s.texture) {
          missingTextureSlots.push(key);
          reasons.push('Missing product texture.');
        } else if (s.textureLoading) {
          loadingSlots.push(key);
          reasons.push('Texture still loading.');
        } else if (s.textureError || !s.textureOk || (s.texture && this._textureErrors[s.texture])) {
          failedSlots.push(key);
          reasons.push('Texture load failure.');
        }
      }, this);
    }

    // Dedupe reasons while preserving order
    var seen = {};
    reasons = reasons.filter(function (r) {
      if (seen[r]) return false;
      seen[r] = true;
      return true;
    });

    return {
      ready: reasons.length === 0,
      reasons: reasons,
      trayId: tray && tray.id ? tray.id : null,
      trayName: tray && tray.name ? tray.name : null,
      variantId: tray && tray.variantId ? tray.variantId : null,
      requiredSlots: req,
      filledSlots: filled,
      failedSlots: failedSlots,
      loadingSlots: loadingSlots,
      missingSlots: missingSlots,
      missingTextureSlots: missingTextureSlots
    };
  };

  WgnSmartTrayEngine.prototype.validateATC = function () {
    var readiness = this.getCheckoutReadiness();
    var ok = !!readiness.ready;
    var tray = this.getActiveTray();

    if (this.atcBtn) {
      // Button UI: business-invalid OR mid-submit → disabled. Never confuse the two in _submitCart.
      this.atcBtn.disabled = !ok || this._submitting;
      this.atcBtn.setAttribute('aria-disabled', ok && !this._submitting ? 'false' : 'true');
    }

    if (this._submitting) {
      // Leave verifying / adding copy set by _submitCart
      return readiness;
    }

    if (ok) {
      if (this.atcText) this.atcText.textContent = 'Add Custom Tray to Cart';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Your custom tray is complete and ready to order!';
    } else if (readiness.reasons.indexOf('Ordering paused by vacation mode.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Ordering paused';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Custom trays cannot be added while vacation mode is active.';
    } else if (!tray || !tray.baseImage || readiness.reasons.indexOf('Missing base image.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Tray base image missing';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Upload a base tray image in Theme Editor.';
    } else if (readiness.reasons.indexOf('Base image still loading.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Verifying tray image…';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Wait for the base tray image to finish loading.';
    } else if (readiness.reasons.indexOf('Base image load failure.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Tray base image failed';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Fix or re-upload the tray base image.';
    } else if (readiness.reasons.indexOf('Invalid polygon layout.') !== -1 || readiness.reasons.indexOf('Missing valid tray definition.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Tray layout invalid';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Fix configurator layout JSON in Theme Editor.';
    } else if (readiness.reasons.indexOf('Missing parent product variant.') !== -1 || readiness.reasons.indexOf('Parent product unavailable.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Tray product unavailable';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Assign a purchasable Shopify product to this tray.';
    } else if (readiness.reasons.indexOf('Texture load failure.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Texture failed (Section ' + (readiness.failedSlots.join(', ') || '?') + ')';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Retry the texture or replace those items before checkout.';
    } else if (readiness.reasons.indexOf('Texture still loading.') !== -1 || readiness.reasons.indexOf('Missing product texture.') !== -1) {
      if (this.atcText) this.atcText.textContent = 'Textures still loading…';
      if (this.atcSubtext) this.atcSubtext.textContent = 'Wait until every section texture finishes loading.';
    } else {
      var remaining = Math.max(0, readiness.requiredSlots - readiness.filledSlots);
      if (this.atcText) this.atcText.textContent = 'Fill ' + remaining + ' more section' + (remaining === 1 ? '' : 's');
      if (this.atcSubtext) this.atcSubtext.textContent = 'Select ' + remaining + ' more item' + (remaining === 1 ? '' : 's') + ' to continue.';
    }
    return readiness;
  };

  WgnSmartTrayEngine.prototype.updatePrice = function () {
    var qty = Math.max(1, parseInt((this.qtyIn && this.qtyIn.value) || '1', 10));
    this._qty = qty;
    var tray = this.getActiveTray();
    if (!this.priceEl) return;
    if (!tray || !(tray.priceCents > 0)) {
      this.priceEl.textContent = '';
      return;
    }
    this.priceEl.textContent = moneyFormat(tray.priceCents * qty, this.currency);
  };

  WgnSmartTrayEngine.prototype.passesFilters = function (card) {
    var t = (card.getAttribute('data-title') || '').toLowerCase();
    var q = '';
    if (this.searchInputs[0]) q = (this.searchInputs[0].value || '').toLowerCase().trim();
    if (q && t.indexOf(q) === -1) {
      var tags = (card.getAttribute('data-tags') || '').toLowerCase();
      if (tags.indexOf(q) === -1) return false;
    }
    if (this.activeCat) {
      if ((card.getAttribute('data-cat') || '').toLowerCase() !== this.activeCat.toLowerCase()) return false;
    }
    if (this.activeTags.size) {
      var tagList = (card.getAttribute('data-tags') || '').toLowerCase().split(',').map(function (s) { return s.trim(); });
      var ok = true;
      this.activeTags.forEach(function (tag) {
        if (tagList.indexOf(tag.toLowerCase()) === -1) ok = false;
      });
      if (!ok) return false;
    }
    return true;
  };

  WgnSmartTrayEngine.prototype.applyFilters = function () {
    if (!this.grid) return;
    var visible = 0;
    var self = this;
    var isFiltered = !!(this.activeCat || this.activeTags.size || (this.searchInputs[0] && this.searchInputs[0].value));
    this.grid.querySelectorAll('[data-card]').forEach(function (card) {
      var sticky = card.classList.contains('sticky');
      var show = sticky || self.passesFilters(card);
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    if (this.catalogCountEl) {
      this.catalogCountEl.textContent = isFiltered
        ? 'Showing ' + visible + ' matching options'
        : 'Showing all ' + visible + ' options';
    }
    if (this.clearFiltersBtn) this.clearFiltersBtn.classList.toggle('is-visible', isFiltered);
    if (this.tabletClearBtn) this.tabletClearBtn.classList.toggle('is-visible', isFiltered);
  };

  WgnSmartTrayEngine.prototype.verifyCompositionImages = function () {
    var self = this;
    var tray = this.getActiveTray();
    if (!this._trayIsPurchasable(tray)) {
      return Promise.reject(new Error('Tray is not purchasable (layout, base image, or product).'));
    }
    var sel = this.getSel();
    var req = this.getRequiredSlots();
    var tasks = [this.ensureImageLoaded(tray.baseImage)];
    var missing = [];
    tray.layout.slots.forEach(function (slot) {
      var s = sel[String(slot.number)];
      if (!s || !s.texture || !s.textureOk) {
        missing.push(String(slot.number));
        return;
      }
      tasks.push(self.ensureImageLoaded(s.texture).catch(function (err) {
        s.textureOk = false;
        s.textureError = true;
        throw new Error('Section ' + slot.number + ' texture failed: ' + (err && err.message ? err.message : err));
      }));
    });
    if (missing.length || this.countFilled() < req) {
      return Promise.reject(new Error('All sections need a successfully loaded master texture before checkout.'));
    }
    return Promise.all(tasks);
  };

  WgnSmartTrayEngine.prototype.renderPreviewPNG = function () {
    var self = this;
    var tray = this.getActiveTray();
    if (!tray || !tray.layout || !tray.baseImage) return Promise.reject(new Error('Missing tray for preview'));
    var vb = tray.layout.viewBox;
    var canvas = document.createElement('canvas');
    canvas.width = Math.round(vb.width);
    canvas.height = Math.round(vb.height);
    var ctx = canvas.getContext('2d');
    var sel = this.getSel();

    return this.ensureImageLoaded(tray.baseImage).then(function (base) {
      ctx.drawImage(base, 0, 0, canvas.width, canvas.height);
      var chain = Promise.resolve();
      tray.layout.slots.forEach(function (slot) {
        var s = sel[String(slot.number)];
        if (!s || !s.texture || !s.textureOk) return;
        chain = chain.then(function () {
          return self.ensureImageLoaded(s.texture).then(function (img) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(slot.points[0][0], slot.points[0][1]);
            for (var i = 1; i < slot.points.length; i++) ctx.lineTo(slot.points[i][0], slot.points[i][1]);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.restore();
          });
        });
      });
      return chain.then(function () {
        return canvas.toDataURL('image/png', 0.85);
      });
    });
  };

  WgnSmartTrayEngine.prototype.uploadToImgbb = function (key, dataUrl) {
    var base64 = dataUrl.split(',')[1];
    var fd = new FormData();
    fd.append('key', key);
    fd.append('image', base64);
    return fetch('https://api.imgbb.com/1/upload', { method: 'POST', body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error('imgbb API error: ' + r.status);
        return r.json();
      })
      .then(function (json) {
        return json && json.data && json.data.url ? json.data.url : null;
      });
  };

  WgnSmartTrayEngine.prototype.addToCart = function (payload) {
    return fetch(this.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart API error: ' + r.status);
      return r.json();
    });
  };

  WgnSmartTrayEngine.prototype._snapshotCheckoutConfig = function () {
    var tray = this.getActiveTray();
    var sel = this.getSel();
    var slotCopy = {};
    Object.keys(sel).forEach(function (k) {
      var s = sel[k];
      if (!s) return;
      slotCopy[k] = {
        title: s.title,
        texture: s.texture,
        textureOk: !!s.textureOk,
        textureError: !!s.textureError,
        textureLoading: !!s.textureLoading,
        variantId: s.variantId || null
      };
    });
    return {
      trayId: tray && tray.id,
      trayName: tray && tray.name,
      variantId: tray && tray.variantId,
      available: tray && tray.available,
      baseImage: tray && tray.baseImage,
      requiredSlots: this.getRequiredSlots(),
      filledSlots: this.countFilled(),
      slots: slotCopy,
      qty: Math.max(1, parseInt((this.qtyIn && this.qtyIn.value) || '1', 10))
    };
  };

  WgnSmartTrayEngine.prototype._buildCartProperties = function (snapshot) {
    var props = {
      'Tray Configuration': snapshot.trayName || 'Custom Built',
      'Tray ID': snapshot.trayId || '',
      'Total Slots': String(snapshot.requiredSlots),
      'Filled Slots': String(snapshot.filledSlots),
      'Configuration Date': new Date().toISOString().split('T')[0]
    };
    Object.keys(snapshot.slots).sort(function (a, b) { return Number(a) - Number(b); }).forEach(function (k) {
      props['Slot ' + k + ' Item'] = snapshot.slots[k].title;
    });
    return props;
  };

  WgnSmartTrayEngine.prototype._submitCart = async function () {
    // Gate on business readiness + duplicate guard — NOT on atcBtn.disabled alone after submit starts
    if (this._submitting) return;
    var pre = this.getCheckoutReadiness();
    if (!pre.ready) {
      this.validateATC();
      return;
    }
    if (!this.atcBtn) return;

    var snapshot = this._snapshotCheckoutConfig();
    this._submitting = true;
    this.atcBtn.disabled = true;
    this.atcBtn.setAttribute('aria-disabled', 'true');
    if (this.atcText) this.atcText.textContent = 'Verifying images…';
    if (this.atcSubtext) this.atcSubtext.textContent = 'Confirming tray images before checkout…';

    try {
      // Hard gate: every required image must decode before any cart network call
      await this.verifyCompositionImages();

      // Re-evaluate BUSINESS readiness only — never use atcBtn.disabled here (it is true while _submitting)
      var readiness = this.getCheckoutReadiness();
      if (!readiness.ready) {
        throw new Error(readiness.reasons[0] || 'Tray is not ready for checkout.');
      }

      // Reject mid-submit tray switches that would mix parent variants
      var live = this.getActiveTray();
      if (!live || live.id !== snapshot.trayId || live.variantId !== snapshot.variantId) {
        throw new Error('Tray selection changed during checkout. Please review your tray and try again.');
      }
      if (this.countFilled() !== snapshot.filledSlots || this.getRequiredSlots() !== snapshot.requiredSlots) {
        throw new Error('Tray configuration changed during checkout. Please review your tray and try again.');
      }

      var props = this._buildCartProperties(snapshot);

      // Optional preview attach — failures must NOT block cart if composition already verified
      try {
        if (this.atcText) this.atcText.textContent = 'Preparing preview…';
        var dataUrl = await this.renderPreviewPNG();
        if (this.imgbbKey && dataUrl) {
          var url = await this.uploadToImgbb(this.imgbbKey, dataUrl);
          if (url) props['Tray Preview Image'] = url;
        }
      } catch (e) {
        console.warn('[WgnSmartTray] optional preview/imgbb skipped:', e);
      }

      if (this.atcText) this.atcText.textContent = 'Adding to Cart...';
      if (!snapshot.variantId) throw new Error('Please configure tray products in section settings.');
      var payload = { items: [{ id: snapshot.variantId, quantity: snapshot.qty, properties: props }] };
      await this.addToCart(payload);
      window.location.href = this.cartUrl;
    } catch (error) {
      this._submitting = false;
      this.render();
      alert(error.message || 'Failed to add custom tray to cart. Please try again.');
      this.validateATC();
    }
  };

  WgnSmartTrayEngine.prototype._bindEvents = function () {
    var self = this;
    var m = this.modal;

    this._on(this.openBtn, 'click', function () { self.openModal(); });
    this._on(this.closeBtn, 'click', function () { self.closeModal(); });

    this._on(m, 'keydown', function (e) {
      if (e.key === 'Escape') { self.closeModal(); return; }
      if (e.key !== 'Tab') return;
      var focusable = Array.from(m.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) {
          var style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    });

    this._on(this.inc, 'click', function () {
      if (!self.qtyIn) return;
      self.qtyIn.value = Math.max(1, (+self.qtyIn.value || 1) + 1);
      self.updatePrice();
    });
    this._on(this.dec, 'click', function () {
      if (!self.qtyIn) return;
      self.qtyIn.value = Math.max(1, (+self.qtyIn.value || 1) - 1);
      self.updatePrice();
    });
    this._on(this.qtyIn, 'input', function () {
      if (+self.qtyIn.value < 1) self.qtyIn.value = 1;
      self.updatePrice();
    });

    this.searchInputs.forEach(function (inp) {
      self._on(inp, 'input', function (e) {
        self.searchInputs.forEach(function (other) { if (other) other.value = e.target.value; });
        self.applyFilters();
      });
    });

    var catContainer = m.querySelector('[data-cats]');
    this._on(catContainer, 'click', function (e) {
      var btn = e.target.closest('.cat-btn, [data-cat]');
      if (!btn) return;
      var cat = btn.getAttribute('data-cat') || null;
      self.activeCat = (self.activeCat && self.activeCat === cat) ? null : cat;
      m.querySelectorAll('[data-cats] .cat-btn, [data-cats] [data-cat]').forEach(function (el) {
        var isAct = (el.getAttribute('data-cat') || '') === (self.activeCat || '');
        el.classList.toggle('active', isAct);
        el.setAttribute('aria-pressed', isAct ? 'true' : 'false');
      });
      if (self.mobileCatSelect) self.mobileCatSelect.value = self.activeCat || '';
      if (self.tabletCatSelect) self.tabletCatSelect.value = self.activeCat || '';
      self.applyFilters();
    });

    function onTagClick(e) {
      var chip = e.target.closest('.chip[data-tag]');
      if (!chip) return;
      var tag = chip.getAttribute('data-tag');
      var will = !chip.classList.contains('active');
      m.querySelectorAll('.chip[data-tag="' + CSS.escape(tag) + '"]').forEach(function (el) {
        el.classList.toggle('active', will);
        el.setAttribute('aria-pressed', will ? 'true' : 'false');
      });
      if (will) self.activeTags.add(tag); else self.activeTags.delete(tag);
      if (self.tagsBadge) {
        self.tagsBadge.textContent = String(self.activeTags.size);
        self.tagsBadge.style.display = self.activeTags.size ? 'inline-block' : 'none';
      }
      if (self.tabletTagsBadge) {
        self.tabletTagsBadge.textContent = String(self.activeTags.size);
        self.tabletTagsBadge.style.display = self.activeTags.size ? 'inline-block' : 'none';
      }
      self.applyFilters();
    }
    m.querySelectorAll('[data-tags]').forEach(function (box) {
      self._on(box, 'click', onTagClick);
    });

    function clearFilters() {
      self.activeCat = null;
      self.activeTags.clear();
      if (self.mobileCatSelect) self.mobileCatSelect.value = '';
      if (self.tabletCatSelect) self.tabletCatSelect.value = '';
      m.querySelectorAll('[data-cats] .cat-btn, [data-cats] [data-cat]').forEach(function (el) {
        var isAll = !(el.getAttribute('data-cat') || '');
        el.classList.toggle('active', isAll);
      });
      m.querySelectorAll('.chip').forEach(function (chip) {
        chip.classList.remove('active');
        chip.setAttribute('aria-pressed', 'false');
      });
      self.searchInputs.forEach(function (inp) { if (inp) inp.value = ''; });
      if (self.tagsBadge) self.tagsBadge.style.display = 'none';
      if (self.tabletTagsBadge) self.tabletTagsBadge.style.display = 'none';
      self.applyFilters();
    }
    this._on(this.clearFiltersBtn, 'click', clearFilters);
    this._on(this.mobileClearBtn, 'click', clearFilters);
    this._on(this.tabletClearBtn, 'click', clearFilters);
    this._on(this.mobileCatSelect, 'change', function (e) {
      self.activeCat = e.target.value || null;
      self.applyFilters();
    });
    this._on(this.tabletCatSelect, 'change', function (e) {
      self.activeCat = e.target.value || null;
      self.applyFilters();
    });
    this._on(this.tagsToggle, 'click', function () {
      var open = self.tagsToggle.classList.toggle('is-open');
      if (self.tagsPanel) self.tagsPanel.style.display = open ? 'block' : 'none';
    });
    this._on(this.tabletTagsToggle, 'click', function () {
      var open = self.tabletTagsToggle.classList.toggle('is-open');
      if (self.tabletTagsPanel) self.tabletTagsPanel.style.display = open ? 'block' : 'none';
    });
    this._on(this.mobileBackBtn, 'click', function () { self.setView('panel'); });
    this._on(this.mobileViewTrayBtn, 'click', function () { self.setView('panel'); });
    this._on(this.contextClearBtn, 'click', function () { self.setActiveSlot(null); });

    this._on(this.sortSelect, 'change', function (e) {
      self.activeSort = e.target.value;
      var cards = Array.from(self.grid.querySelectorAll('[data-card]'));
      cards.sort(function (a, b) {
        if (self.activeSort === 'name-asc') return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
        if (self.activeSort === 'name-desc') return (b.getAttribute('data-title') || '').localeCompare(a.getAttribute('data-title') || '');
        return (Number(a.getAttribute('data-original-index')) || 0) - (Number(b.getAttribute('data-original-index')) || 0);
      });
      cards.forEach(function (c) { self.grid.appendChild(c); });
    });

    // Tray interactions (delegation)
    this._on(this.trayMount, 'click', function (e) {
      var poly = e.target.closest('.tray-sec');
      var marker = e.target.closest('.slot-marker');
      var target = poly || marker;
      if (!target) return;
      var idx = target.getAttribute('data-slot') || target.getAttribute('data-marker');
      self.setActiveSlot(idx);
      if (self.pendingCard) {
        self.place(self.pendingCard, idx);
        self.pendingCard = null;
      } else {
        if (self.isMobile()) self.setView('catalog');
        var item = self.getSel()[idx];
        self._setHint(
          item
            ? 'Section ' + idx + ' selected (' + item.title + '). Click a product below to replace it.'
            : 'Section ' + idx + ' selected. Choose a product to place in this section.',
          'info'
        );
      }
    });

    this._on(this.summaryMount, 'click', function (e) {
      var row = e.target.closest('.slot-summary-row');
      if (!row) return;
      var slotIdx = row.getAttribute('data-slot-row');
      if (e.target.closest('[data-row-btn-remove]')) { e.stopPropagation(); self.removeSlot(slotIdx); return; }
      if (e.target.closest('[data-row-btn-retry]')) {
        e.stopPropagation();
        var item = self.getSel()[slotIdx];
        if (item && item.texture) self.retryTexture(item.texture, slotIdx);
        return;
      }
      if (e.target.closest('[data-row-btn-change]') || e.target.closest('[data-row-btn-add]')) {
        e.stopPropagation();
        self.setActiveSlot(slotIdx);
        if (self.isMobile()) self.setView('catalog');
        return;
      }
      self.setActiveSlot(slotIdx);
      if (self.isMobile()) self.setView('catalog');
    });

    this._on(this.grid, 'click', function (e) {
      var card = e.target.closest('[data-card]');
      if (!card || card.classList.contains('sold-out') || card.classList.contains('is-unavailable')) return;
      if (self.activeSlot) {
        self.place(card, self.activeSlot);
      } else {
        var emptyIdx = self.findNextEmptySlot();
        if (emptyIdx) self.place(card, emptyIdx);
        else {
          self.pendingCard = card;
          self._setHint('All sections are filled. Click any section on the tray or in the summary to replace it.', 'info');
          if (self.isMobile()) self.setView('panel');
        }
      }
    });

    this._on(this.atcBtn, 'click', function () { self._submitCart(); });

    this._on(window, 'resize', function () {
      if (!self.modal.classList.contains('open')) return;
      clearTimeout(self._resizeT);
      self._resizeT = setTimeout(function () {
        if (self.isMobile()) {
          if (!self.modal.getAttribute('data-view')) self.setView('panel');
        } else self.modal.removeAttribute('data-view');
      }, 100);
    });
  };

  function bootFromRoot(root) {
    if (!root || root.getAttribute('data-wgn-initialized') === '1') return null;
    var raw = root.getAttribute('data-wgn-boot');
    var boot;
    try {
      boot = raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('[WgnSmartTray] Invalid boot JSON', e);
      return null;
    }
    if (!boot) return null;
    root.setAttribute('data-wgn-initialized', '1');
    return new WgnSmartTrayEngine(root, boot);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-wgn-smart-tray]');
    var engines = [];
    roots.forEach(function (root) {
      var eng = bootFromRoot(root);
      if (eng) engines.push(eng);
    });
    return engines;
  }

  // Theme Editor section reload support — destroy listeners + page-active class
  document.addEventListener('shopify:section:load', function (e) {
    if (!e || !e.target) return;
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e || !e.target) return;
    e.target.querySelectorAll('[data-wgn-smart-tray]').forEach(function (root) {
      var eng = activeEngines.find(function (x) { return x.root === root; });
      if (eng) eng.destroy();
      else {
        root.removeAttribute('data-wgn-initialized');
        root.removeAttribute('data-hide-main-product');
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  global[NS] = {
    parseAndValidateTrayLayout: parseAndValidateTrayLayout,
    initAll: initAll,
    Engine: WgnSmartTrayEngine,
    loadImage: loadImage,
    activeEngines: activeEngines
  };
})(typeof window !== 'undefined' ? window : this);
